import styled from 'styled-components';
import { Modal } from 'design-system/src/components/Modal';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import { createTrackingProps } from 'shared-utils/analytics';

export type DefaultFiltersNewFeatureModalProps = {
  onClose: () => void;
  onLinkClick: () => void;
};

// Container for the two sides of the modal
const Content = styled.div`
  display: flex;
  height: 100%;
  overflow: auto;

  @media (max-width: ${breakpoints.smallDesktopMax}px) {
    flex-direction: column;
  }
`;

const PurpleSide = styled.div`
  background-color: var(--purple-200);
  padding: 64px 32px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PurpleHeading = styled(Typography).attrs({ renderedAs: 'h1' })`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WhiteSide = styled.div`
  background-color: var(--white);
  padding: 48px 32px 32px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between; /* push text and buttons apart */
  // minimum gap between text sections and buttons for mobile
  gap: 48px;
`;

const TextSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const DefaultFiltersNewFeatureModal = ({ onClose, onLinkClick }: DefaultFiltersNewFeatureModalProps) => {
  return (
    <Modal onClose={onClose} maxWidth="734px">
      <Content {...createTrackingProps({ unmask: true })}>
        <PurpleSide>
          <PurpleHeading>
            <Typography styledAs="h6" color="var(--purple-900)">
              Introducing:
            </Typography>
            <Typography styledAs="h3" color="var(--purple-700)">
              Notification Preferences
            </Typography>
          </PurpleHeading>
        </PurpleSide>
        <WhiteSide>
          <TextSections>
            <TextSection>
              <Typography styledAs="bodySpaceGrotesk" renderedAs="h2" weight={500} color="var(--purple-700)">
                What's new
              </Typography>
              <Typography styledAs="bodySpaceGrotesk">
                Save time with personalized provider filtering! You can now customize your portal to automatically show
                only the providers you work with when you log in - no more manual filtering every time. You can set
                these filters alongside your email notifications to ensure you always see all relevant updates.
              </Typography>
            </TextSection>
            <TextSection>
              <Typography styledAs="bodySpaceGrotesk" renderedAs="h2" weight={500} color="var(--purple-700)">
                Where to find it
              </Typography>
              <Typography styledAs="bodySpaceGrotesk">
                Navigate to the <i>Account</i> tab in the bottom-left corner, then click <i>Settings</i>. Under{' '}
                <i>Notifications & Filters</i>, you'll be able to select your provider preferences.
              </Typography>
            </TextSection>
          </TextSections>
          <ButtonRow align={Alignment.Left}>
            <Button size="sm" onClick={onLinkClick}>
              Go to Settings
            </Button>
            <Button size="sm" variant="tertiary" onClick={onClose}>
              Close
            </Button>
          </ButtonRow>
        </WhiteSide>
      </Content>
    </Modal>
  );
};

export default DefaultFiltersNewFeatureModal;
